import apiClient from './client';


export const apiService = {
    login: (email, code) => {
        return apiClient.post('/signin', { email, code });
    },

    sendCode: (email) => {
        return apiClient.post('/send-code', { email });
    },

    getCurrentUser: () => {
        return apiClient.get('/me');
    },

    getSubscription: () => {
        return apiClient.get('/check-subscription');
    },

    logout: async () => {
        try {
            await apiClient.post('/logout');
        } finally {
            localStorage.removeItem('sessionId');
            localStorage.removeItem('user');
            window.location.href = '/signin';
        }
    },
    //--------------------------------------------------

    saveDraft: (postData) => {
        return apiClient.post('/posts/draft', postData);
    },
    deleteDraft: (postId) => {
        return apiClient.delete(`/posts/draft?id=${postId}`);
    },
    publishPost: (postData) => {
        return apiClient.post('/posts/publish', postData);
    },
    listPosts: (status = 'all', page = 1, limit = 6, timeFilter) => {
        return apiClient.post("/posts/list", {
            status, page, limit, timeFilter
        });
    },
    getPostDetail: (postId) => {
        return apiClient.post(`/post/detail`, { postId });
    },

    getPostFeed: (external_id, accountId) => {
        return apiClient.get(`/feed?id=${external_id}&account_id=${accountId}`);
    },
    getPublishStatus: (postId) => {
        return apiClient.post(`/post/status`, { postId });
    },

    getTikTokCreatorInfo: (accountId) => {
        return apiClient.get(`/tiktok_creator_info?accountId=${accountId}`, {
            _skipAuthInterceptor: true
        });
    },
    //----------------------------------------------

    getAuthUrl: (platform) => {
        const type = platform === "linkedinB" ? "business" : (platform === "linkedin" ? "personal" : "");
        return apiClient.get(`/auth/url?platform=${platform}&type=${type}`);
    },

    callbackAuth: (platform, code, state) => {
        return apiClient.post(`/auth/callback`, { code, platform, state });
    },

    refreshToken: (platform, code) => {
        return apiClient.post(`/auth/refresh`, { platform, code });
    },

    confirmAccounts: (tempId, selectIds, platform) => {
        return apiClient.post(`/auth/selection`, { tempId, selectIds, platform });
    },

    submitToken: (platform, data) => {
        return apiClient.post(`/auth/submit-token`, { platform, data });
    },
    //---------------------------------------------------

    deleteAccount: (accountId) => {
        return apiClient.delete(`/account?accountId=${accountId}`);
    },
    reconnectAccount: (accountId) => {
        return apiClient.post(`/account/reconnect`, { accountId });
    },
    listAccounts: () => {
        return apiClient.get(`/accounts`);
    },

    /*getComments: (external_id, account_id, next, is_reply) => {
        return apiClient.get(`/comments?external_id=${external_id}&account_id=${account_id}&next=${next}&is_reply=${is_reply}`);
    },

    replyComment: (external_id, account_id, content) => {
        return apiClient.post(`/comment/reply`, { external_id, account_id, content });
    },*/

    //----------------------------------------------------

    deleteFile: (key) => {
        return apiClient.delete(`/delete-file?key=${key}`);
    },
    getFilesForUser: (page) => {
        return apiClient.get(`/get-files?page=${page}`);
    },

    //----------------------------------------------------
    cancelSubscription: () => {
        return apiClient.post(`/cancel-subscription`, {});
    },
    createSubscription: (plan) => {
        return apiClient.post(`/create-checkout`, { plan });
    },
    changeSubscription: (newPlan) => {
        return apiClient.post(`/update-subscription`, { newPlan });
    },
    AIRewriteContent: (content) => {
        return apiClient.post(`/ai-rewrite`, { content });
    },
    AIGenerateTags: (content) => {
        return apiClient.post(`/ai-tags`, { content });
    },
    createApiKey: (name) => apiClient.post('/api-keys', { name }),
    listApiKeys: () => apiClient.get('/api-keys'),
    revokeApiKey: (id) => apiClient.delete(`/api-keys/${encodeURIComponent(id)}`),
    estimateAgent: (brief) => apiClient.post('/ai-agents/estimate', brief),
    createAgent: (brief, schedule) => apiClient.post('/ai-agents', { brief, schedule }),
    listAgents: () => apiClient.get('/ai-agents'),
    updateAgent: (id, patch) => apiClient.patch(`/ai-agents/${id}`, patch),
    deleteAgent: (id) => apiClient.delete(`/ai-agents/${id}`),
    generateAgentPost: (id) => apiClient.post(`/ai-agents/${id}/generate`, {}),
    listTodayAgentPosts: () => apiClient.get('/ai-agents/posts/today'),
    updateAgentPost: (id, patch) => apiClient.patch(`/ai-agents/posts/${id}`, patch),
    approveAgentPost: (id) => apiClient.post(`/ai-agents/posts/${id}/approve`, {}),
    cancelAgentPost: (id) => apiClient.post(`/ai-agents/posts/${id}/cancel`, {}),
    getAgentUsage: () => apiClient.get('/ai-agents/usage'),
};

export async function smartUpload(file, fileName = null, needThumbnail = true) {
    if (!(file instanceof Blob)) {
        throw new TypeError('Upload expects a File or Blob.');
    }
    const token = localStorage.getItem('sessionId');
    if (!token) {
        throw new Error("No session found, please login.");
    }
    const authFetch = (url, options = {}) => {
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const mimeExtensions = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/gif': 'gif',
        'video/mp4': 'mp4',
        'video/quicktime': 'mov',
        'video/webm': 'webm',
    };
    const extensionFromName = typeof file.name === 'string' && file.name.includes('.')
        ? file.name.split('.').pop().toLowerCase()
        : '';
    const extension = extensionFromName || mimeExtensions[file.type] || 'bin';
    const uploadFile = file instanceof File
        ? file
        : new File([file], `upload-${Date.now()}.${extension}`, { type: file.type || 'application/octet-stream' });

    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hash = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

    const fileKey = fileName ? fileName : `${hash}.${extension}`;
    const size = file.size;
    const type = file.type;

    console.log(`Checking hash: ${hash}`);
    const checkRes = await authFetch(`https://as.little-start-rs2025.workers.dev/check-file?file_key=${fileKey}&size=${size}&type=${type}`);

    if (checkRes.status === 401) throw new Error("Session expired");
    const checkData = await checkRes.json();

    if (!checkData.canUpload) {
        alert("Upload limit reached. Please delete some files from your gallery to free up space.");
        return;
    }
    if (checkData.exists) {
        console.log("file exist");
        return checkData.url;
    }

    const formData = new FormData();
    formData.append('file', uploadFile, uploadFile.name);
    formData.append('hash', hash);
    formData.append('need_thumbnail', needThumbnail);
    if (fileName !== null) {
        formData.append('file_name', fileName);
    }

    const uploadRes = await authFetch('https://as.little-start-rs2025.workers.dev/upload', {
        method: 'POST',
        body: formData
    });

    if (!uploadRes.ok)
        throw new Error(JSON.stringify({
            code: resData.code || 'HTTP_ERROR',
            message: resData.error
        }));

    const uploadData = await uploadRes.json();
    return uploadData.url;
}
